import { useWorkbook } from '@/context/WorkbookContext';
import { WorkbookPage, SectionTitle, Paragraph, WorksheetInput, AffirmationBox } from '@/components/WorkbookPage';
import { RefreshCcw, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';

export const ResetPage = () => {
  const { resetWorkbook } = useWorkbook();
  
  return (
    <WorkbookPage pageId="reset-page" prevPage="crisis-mode" nextPage="program-description">
      <header className="mb-10">
        <div className="flex items-center gap-3 mb-4">
          <RefreshCcw className="h-6 w-6 text-primary" />
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground">
            Reset Page
          </h1>
        </div>
        <p className="text-muted-foreground">
          Starting fresh when you need to begin again
        </p>
      </header>
      
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-6 mb-8">
        <div className="flex items-start gap-4">
          <Heart className="h-6 w-6 text-primary shrink-0 mt-1" />
          <div>
            <h3 className="font-semibold mb-2">It's Okay to Start Over</h3>
            <Paragraph className="mb-0">
              Maybe you fell off track. Maybe life happened. Maybe you're coming back to this 
              workbook after months or years away. <strong>That's okay.</strong> What matters 
              is that you're here now, ready to try again.
            </Paragraph>
          </div>
        </div>
      </div>
      
      <SectionTitle>When to Use This Page</SectionTitle>
      
      <ul className="space-y-3 my-6">
        <li className="flex items-start gap-3">
          <span className="text-primary">•</span>
          <span>Your circumstances have changed significantly (new job, move, life event)</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="text-primary">•</span>
          <span>You stopped using the workbook and want to restart with fresh energy</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="text-primary">•</span>
          <span>You've completed a major goal and want to set new ones</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="text-primary">•</span>
          <span>You went through a crisis and need to rebuild from scratch</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="text-primary">•</span>
          <span>A new year, new chapter, or fresh start feels right</span>
        </li>
      </ul>
      
      <SectionTitle>Before You Reset</SectionTitle>
      
      <Paragraph>
        Take a moment to reflect on what you learned, even if you didn't complete everything:
      </Paragraph>
      
      <div className="bg-muted/30 rounded-xl p-6 my-6 border border-border space-y-6">
        <WorksheetInput
          label="What worked well when I was following the workbook?"
          placeholder="What strategies or habits helped?"
          multiline
        />
        
        <WorksheetInput
          label="What got in my way or caused me to stop?"
          placeholder="Be honest—no judgment"
          multiline
        />
        
        <WorksheetInput
          label="What will I do differently this time?"
          placeholder="How will you set yourself up for success?"
          multiline
        />
        
        <WorksheetInput
          label="One thing I'm proud of, even if small:"
          placeholder="Celebrate any progress you made"
        />
      </div>
      
      <SectionTitle>Ready to Begin Again?</SectionTitle>
      
      <Paragraph>
        If you want to clear your workbook data and start completely fresh, you can use the 
        button below. This will erase all your saved responses. (Your data is stored locally 
        in your browser.)
      </Paragraph>
      
      <div className="my-8 p-6 bg-destructive/5 border border-destructive/20 rounded-xl text-center">
        <p className="text-sm text-muted-foreground mb-4">
          This action cannot be undone. All your progress and saved data will be erased.
        </p>
        <Button
          variant="destructive"
          onClick={resetWorkbook}
          className="gap-2"
        >
          <RefreshCcw className="h-4 w-4" />
          Reset Workbook Data
        </Button>
      </div>
      
      <AffirmationBox>
        Every day is a new opportunity. I release the past without shame and step forward 
        with hope. I am ready to begin again.
      </AffirmationBox>
      
      <div className="mt-8 text-center p-6 bg-secondary/50 rounded-xl">
        <p className="font-serif text-lg italic text-foreground mb-2">
          "Fall seven times, stand up eight."
        </p>
        <p className="text-sm text-muted-foreground">— Japanese Proverb</p>
      </div>
    </WorkbookPage>
  );
};
